import { motion, useInView, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef, useState } from "react";
import {
    CpuChipIcon,
    ShieldCheckIcon,
    BoltIcon,
    GlobeAltIcon,
    CheckIcon,
    SparklesIcon,
    RocketLaunchIcon,
    Cog8ToothIcon,
    ChartBarIcon,
} from "@heroicons/react/24/outline";

const steps = [
    {
        number: "01",
        title: "Knowledge Base Setup",
        description: "Upload your documents, crawl your website, or connect your existing knowledge sources. Our AI automatically processes and indexes everything.",
        icon: CpuChipIcon,
        color: "from-blue-500 to-cyan-500",
        bgGlow: "bg-blue-500/20",
        features: [
            "Document processing (PDF, Word, Excel)",
            "Website crawling and indexing",
            "FAQ and knowledge base import",
            "Automatic content categorization"
        ]
    },
    {
        number: "02",
        title: "Workflow Design",
        description: "Design flows with directional arrows, insert nodes inline, and reconnect edges seamlessly.",
        icon: Cog8ToothIcon,
        color: "from-purple-500 to-pink-500",
        bgGlow: "bg-purple-500/20",
        features: [
            "Interactive builder with edge update & cut",
            "Conditional logic (AND/OR) and priorities",
            "Backend-driven node configs",
            "API calls and LLM steps"
        ]
    },
    {
        number: "03",
        title: "AI Training & Testing",
        description: "Use your knowledge base with RAG. Test runs publish versions and return execution paths.",
        icon: BoltIcon,
        color: "from-green-500 to-emerald-500",
        bgGlow: "bg-green-500/20",
        features: [
            "LLM (OpenAI) integration",
            "Real-time tests and publish",
            "Execution path visibility",
            "Continuous optimization"
        ]
    },
    {
        number: "04",
        title: "Deploy on Chat or Voice",
        description: "Embed the chat widget with one script tag, or attach a phone number for the voice agent. Monitor analytics and iterate with revisions.",
        icon: RocketLaunchIcon,
        color: "from-orange-500 to-red-500",
        bgGlow: "bg-orange-500/20",
        features: [
            "One-line website integration for chat",
            "Bring your own number or buy one in-app for voice",
            "Real-time analytics for both channels",
            "Revisions & rollback"
        ]
    }
];

const technicalFeatures = [
    {
        title: "Advanced AI Architecture",
        description: "Built on cutting-edge language models with RAG (Retrieval-Augmented Generation) for context-aware responses.",
        icon: CpuChipIcon,
        color: "from-purple-500 to-pink-500",
        delay: 0,
    },
    {
        title: "Vector Database",
        description: "Powered by Qdrant for lightning-fast semantic search and intelligent document retrieval.",
        icon: SparklesIcon,
        color: "from-blue-500 to-cyan-500",
        delay: 0.1,
    },
    {
        title: "Real-time Processing",
        description: "WebSocket-based architecture ensures instant responses and seamless user experience.",
        icon: BoltIcon,
        color: "from-green-500 to-emerald-500",
        delay: 0.2,
    },
    {
        title: "Enterprise Security",
        description: "SOC 2-aligned controls with encryption at rest, role-based access control, and data isolation.",
        icon: ShieldCheckIcon,
        color: "from-gray-600 to-gray-800",
        delay: 0.3,
    },
    {
        title: "Global CDN",
        description: "Distributed infrastructure ensures 99.9% uptime and fast response times worldwide.",
        icon: GlobeAltIcon,
        color: "from-teal-500 to-blue-500",
        delay: 0.4,
    },
    {
        title: "Scalable Infrastructure",
        description: "Auto-scaling architecture handles millions of conversations without performance degradation.",
        icon: ChartBarIcon,
        color: "from-orange-500 to-red-500",
        delay: 0.5,
    }
];

/* ==================== AI-POWERED FLOATING ORBS ==================== */
type FloatingOrbProps = { className?: string; delay?: number };
const FloatingOrb = ({ className = "", delay = 0 }: FloatingOrbProps) => (
    <motion.div
        className={`absolute rounded-full blur-3xl will-change-transform ${className}`}
        animate={{
            x: [0, 60, -40, 30, 0],
            y: [0, -50, 40, -20, 0],
            scale: [1, 1.2, 0.8, 1.1, 1],
            rotate: [0, 180, 360],
        }}
        transition={{
            duration: 20,
            delay,
            repeat: Infinity,
            ease: "easeInOut"
        }}
        style={{
            background: 'radial-gradient(circle at center, currentColor 0%, transparent 70%)'
        }}
    />
);

/* ==================== AI NEURAL NETWORK CONNECTIONS ==================== */
const NeuralConnection = ({ from, to, delay = 0, isVisible }: { 
    from: { x: number; y: number }; 
    to: { x: number; y: number }; 
    delay?: number;
    isVisible: boolean;
}) => {
    const length = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));
    const angle = Math.atan2(to.y - from.y, to.x - from.x) * (180 / Math.PI);

    return (
        <motion.div
            className="absolute pointer-events-none"
            style={{
                left: from.x,
                top: from.y,
                transform: `rotate(${angle}deg)`,
                transformOrigin: '0 50%',
            }}
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ delay, duration: 0.8 }}
        >
            {/* Main connection line */}
            <motion.div
                className="h-0.5 bg-gradient-to-r from-blue-400/40 via-purple-400/60 to-cyan-400/40"
                initial={{ width: 0 }}
                animate={isVisible ? { width: length } : { width: 0 }}
                transition={{ delay: delay + 0.2, duration: 1.2, ease: "easeOut" }}
            />
            
            {/* Animated data pulse */}
            <motion.div
                className="absolute top-0 left-0 h-0.5 w-3 bg-gradient-to-r from-transparent via-blue-300 to-transparent"
                animate={isVisible ? {
                    x: [0, length - 12, 0],
                    opacity: [0, 1, 0],
                } : {}}
                transition={{
                    delay: delay + 1.5,
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                    ease: "easeInOut"
                }}
            />
        </motion.div>
    );
};

/* ==================== ENHANCED STEP CARD WITH AI GRAPH NODE BEHAVIOR ==================== */
type Step = {
    number: string;
    title: string;
    description: string;
    icon: any;
    color: string;
    bgGlow: string;
    features: string[];
};

type StepCardProps = { step: Step; index: number; isVisible: boolean };
const StepCard = ({ step, index, isVisible }: StepCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    
    // AI node entrance animation - cards orbit before settling
    const orbitRadius = 150;
    const orbitAngle = (index * 90) - 180; // Different starting positions
    const orbitX = Math.cos(orbitAngle * Math.PI / 180) * orbitRadius;
    const orbitY = Math.sin(orbitAngle * Math.PI / 180) * orbitRadius;
    
    return (
        <motion.div
            ref={cardRef}
            initial={{ 
                opacity: 0, 
                x: orbitX, 
                y: orbitY, 
                scale: 0.3,
                rotateY: -45,
                rotateX: 20
            }}
            animate={isVisible ? { 
                opacity: 1, 
                x: 0, 
                y: 0, 
                scale: 1,
                rotateY: 0,
                rotateX: 0,
                transition: { 
                    delay: index * 0.3,
                    duration: 1.2,
                    ease: [0.23, 1, 0.32, 1], // Custom bezier for AI-like smoothness
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                }
            } : {}}
            className="relative group will-change-transform"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ perspective: '1000px' }}
        >
            {/* AI Graph Node - Pulsing connection point */}
            <motion.div 
                className="absolute -left-6 top-12 z-20"
                initial={{ scale: 0 }}
                animate={isVisible ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: index * 0.3 + 0.8, duration: 0.6 }}
            >
                <div className="relative">
                    {/* Outer pulse ring */}
                    <motion.div
                        className="absolute inset-0 w-4 h-4 rounded-full bg-blue-400/30"
                        animate={{
                            scale: isHovered ? [1, 2.5, 1] : [1, 1.8, 1],
                            opacity: [0.6, 0, 0.6],
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                    />
                    
                    {/* Core node */}
                    <motion.div
                        className="relative w-4 h-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full shadow-lg border-2 border-white/20"
                        animate={{
                            boxShadow: isHovered 
                                ? ["0 0 0 0 rgba(59, 130, 246, 0.7)", "0 0 20px 5px rgba(59, 130, 246, 0.3)", "0 0 0 0 rgba(59, 130, 246, 0.7)"]
                                : "0 4px 15px rgba(59, 130, 246, 0.3)"
                        }}
                        transition={{ duration: 1.5, repeat: isHovered ? Infinity : 0 }}
                    />
                </div>
            </motion.div>

            {/* Main Card with AI Glassmorphism */}
            <motion.div
                className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-2xl p-8 will-change-transform"
                style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                }}
                whileHover={{
                    y: -12,
                    rotateX: 5,
                    rotateY: isHovered ? 2 : 0,
                    transition: { duration: 0.4, ease: "easeOut" }
                }}
                animate={{
                    boxShadow: isHovered 
                        ? "0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 30px rgba(59, 130, 246, 0.1)"
                        : "0 10px 25px -5px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Animated background mesh gradient */}
                <motion.div
                    className="absolute inset-0 opacity-0"
                    animate={{ 
                        opacity: isHovered ? 0.05 : 0,
                        background: isHovered 
                            ? `radial-gradient(600px circle at 50% 50%, ${step.color.includes('blue') ? 'rgba(59, 130, 246, 0.1)' : step.color.includes('purple') ? 'rgba(139, 92, 246, 0.1)' : step.color.includes('green') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(249, 115, 22, 0.1)'}, transparent 50%)`
                            : 'transparent'
                    }}
                    transition={{ duration: 0.6 }}
                />

                {/* AI Neural Network Pattern Overlay */}
                {isHovered && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                        {[...Array(5)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-1 h-1 bg-blue-300/40 rounded-full"
                                initial={{ 
                                    x: Math.random() * 100 + '%',
                                    y: Math.random() * 100 + '%',
                                    opacity: 0 
                                }}
                                animate={{
                                    opacity: [0, 0.8, 0],
                                    scale: [0.5, 1.5, 0.5],
                                }}
                                transition={{
                                    duration: 3,
                                    delay: i * 0.3,
                                    repeat: Infinity,
                                    repeatType: "loop"
                                }}
                            />
                        ))}
                    </div>
                )}

                <div className="relative z-10 flex items-start gap-6">
                    {/* Enhanced Number Badge with AI Glow */}
                    <motion.div
                        className={`relative h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${step.color} text-white text-xl font-bold shadow-2xl will-change-transform`}
                        whileHover={{ 
                            scale: 1.1,
                            rotate: [0, -10, 10, 0],
                            transition: { duration: 0.6 }
                        }}
                    >
                        {step.number}
                        
                        {/* Animated glow ring */}
                        <motion.div
                            className="absolute inset-0 rounded-2xl"
                            style={{ 
                                background: `linear-gradient(135deg, ${step.color.replace('from-', '').replace(' to-', ', ')})`,
                                filter: 'blur(8px)',
                            }}
                            animate={{
                                scale: isHovered ? [1, 1.3, 1] : 1,
                                opacity: isHovered ? [0.3, 0.6, 0.3] : 0.3,
                            }}
                            transition={{ duration: 2, repeat: isHovered ? Infinity : 0 }}
                        />
                    </motion.div>
                    
                    <div className="flex-1">
                        {/* Enhanced Typography with Stagger */}
                        <motion.h3 
                            className="text-2xl font-bold text-white mb-3 tracking-tight bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isVisible ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: index * 0.3 + 0.4, duration: 0.6 }}
                        >
                            {step.title}
                        </motion.h3>
                        
                        <motion.p 
                            className="text-slate-300/90 mb-6 leading-relaxed"
                            initial={{ opacity: 0, y: 20 }}
                            animate={isVisible ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: index * 0.3 + 0.5, duration: 0.6 }}
                        >
                            {step.description}
                        </motion.p>
                        
                        {/* Enhanced Feature List with Check Animations */}
                        <motion.ul 
                            className="space-y-3"
                            initial={{ opacity: 0 }}
                            animate={isVisible ? { opacity: 1 } : {}}
                            transition={{ delay: index * 0.3 + 0.6, duration: 0.6 }}
                        >
                            {step.features.map((feature: string, featureIndex: number) => (
                                <motion.li 
                                    key={featureIndex} 
                                    className="flex items-center text-sm text-slate-300/90"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={isVisible ? { opacity: 1, x: 0 } : {}}
                                    transition={{ 
                                        delay: index * 0.3 + 0.7 + featureIndex * 0.1,
                                        duration: 0.5,
                                        ease: "easeOut"
                                    }}
                                >
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={isVisible ? { scale: 1 } : {}}
                                        transition={{ 
                                            delay: index * 0.3 + 0.8 + featureIndex * 0.1,
                                            duration: 0.3,
                                            type: "spring",
                                            stiffness: 200
                                        }}
                                    >
                                        <CheckIcon className="h-4 w-4 text-emerald-400 mr-3 flex-shrink-0" />
                                    </motion.div>
                                    {feature}
                                </motion.li>
                            ))}
                        </motion.ul>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

/* ==================== ENHANCED TECH CARD WITH DEPTH ==================== */
type Feature = { title: string; description: string; icon: any; color: string; delay: number };
type TechCardProps = { feature: Feature; index: number; isVisible: boolean };
const TechCard = ({ feature, index: _index, isVisible }: TechCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
        <motion.div
            initial={{ 
                opacity: 0, 
                y: 100, 
                rotateX: 45,
                scale: 0.8 
            }}
            animate={isVisible ? {
                opacity: 1,
                y: 0,
                rotateX: 0,
                scale: 1,
                transition: {
                    delay: feature.delay,
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    type: "spring",
                    stiffness: 100
                }
            } : {}}
            className="group relative will-change-transform"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{ perspective: '1000px' }}
        >
            <motion.div
                className="relative h-full rounded-2xl p-8 border border-white/10 backdrop-blur-2xl will-change-transform"
                style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                }}
                whileHover={{
                    y: -16,
                    rotateX: 8,
                    rotateY: 5,
                    transition: { duration: 0.4 }
                }}
                animate={{
                    boxShadow: isHovered 
                        ? "0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 25px rgba(139, 92, 246, 0.15)"
                        : "0 10px 25px -5px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Enhanced Icon with 3D Effect */}
                <motion.div
                    className={`relative h-14 w-14 flex items-center justify-center rounded-xl bg-gradient-to-r ${feature.color} shadow-2xl mb-6 will-change-transform`}
                    animate={{
                        rotateY: isHovered ? 360 : 0,
                        rotateX: isHovered ? [0, 10, 0] : 0,
                    }}
                    transition={{ duration: 1.2, ease: "easeInOut" }}
                >
                    <feature.icon className="h-7 w-7 text-white relative z-10" />
                    
                    {/* 3D depth shadow */}
                    <motion.div
                        className="absolute inset-0 rounded-xl bg-black/20 translate-y-1 translate-x-1"
                        animate={{
                            opacity: isHovered ? 0.3 : 0.1,
                        }}
                    />
                    
                    {/* Glow effect */}
                    <motion.div
                        className="absolute inset-0 rounded-xl"
                        style={{ 
                            background: `linear-gradient(135deg, ${feature.color.replace('from-', '').replace(' to-', ', ')})`,
                            filter: 'blur(6px)',
                        }}
                        animate={{
                            scale: isHovered ? [1, 1.4, 1] : 1,
                            opacity: isHovered ? [0.2, 0.8, 0.2] : 0.2,
                        }}
                        transition={{ duration: 2, repeat: isHovered ? Infinity : 0 }}
                    />
                </motion.div>
                
                <h4 className="text-xl font-semibold text-white mb-3 tracking-tight">
                    {feature.title}
                </h4>
                <p className="text-slate-300/90 leading-relaxed">
                    {feature.description}
                </p>

                {/* Subtle border glow on hover */}
                <motion.div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    animate={{
                        boxShadow: isHovered 
                            ? `inset 0 0 60px ${feature.color.includes('purple') ? 'rgba(139, 92, 246, 0.1)' : feature.color.includes('blue') ? 'rgba(59, 130, 246, 0.1)' : feature.color.includes('green') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(234, 88, 12, 0.1)'}`
                            : 'inset 0 0 0px transparent'
                    }}
                    transition={{ duration: 0.4 }}
                />
            </motion.div>
        </motion.div>
    );
};

/* ==================== MAIN COMPONENT ==================== */
export default function HowItWorks() {
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const stepsRef = useRef<HTMLDivElement | null>(null);
    const techRef = useRef<HTMLDivElement | null>(null);
    const codeRef = useRef<HTMLDivElement | null>(null);
    
    const isSectionVisible = useInView(sectionRef, { once: false, amount: 0.1 });
    const isStepsVisible = useInView(stepsRef, { once: false, amount: 0.2 });
    const isTechVisible = useInView(techRef, { once: false, amount: 0.2 });
    const isCodeVisible = useInView(codeRef, { once: false, amount: 0.3 });

    // Enhanced parallax with spring physics
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
    const parallaxY = useSpring(useTransform(scrollYProgress, [0, 1], [40, -40]), { stiffness: 100, damping: 30 });
    const parallaxScale = useSpring(useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]), { stiffness: 100, damping: 30 });

    return (
        <div id="how-it-works" ref={sectionRef} className="relative py-32 bg-slate-900 overflow-hidden">
            {/* Enhanced AI Floating Orbs with Better Performance */}
            <motion.div 
                style={{ y: parallaxY, scale: parallaxScale }} 
                className="pointer-events-none absolute inset-0 -z-10 will-change-transform"
            >
                <FloatingOrb className="-left-40 top-20 h-96 w-96 text-indigo-500/20" delay={0} />
                <FloatingOrb className="-right-32 top-40 h-80 w-80 text-purple-500/20" delay={2} />
                <FloatingOrb className="left-1/2 bottom-20 h-72 w-72 text-blue-500/20" delay={4} />
                <FloatingOrb className="-left-20 bottom-10 h-64 w-64 text-cyan-500/20" delay={6} />
                <FloatingOrb className="right-1/4 top-1/3 h-56 w-56 text-emerald-500/15" delay={8} />
            </motion.div>

            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                {/* Enhanced Header with AI Typography */}
                <motion.div 
                    style={{ y: parallaxY }}
                    className="mx-auto max-w-3xl text-center"
                    initial={{ opacity: 0 }}
                    animate={isSectionVisible ? { opacity: 1 } : {}}
                    transition={{ duration: 1.2 }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.9 }}
                        animate={isSectionVisible ? { opacity: 1, y: 0, scale: 1 } : {}}
                        transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                    >
                        <h2 className="text-5xl font-bold tracking-tight text-white sm:text-6xl bg-gradient-to-r from-white via-blue-100 to-blue-200 bg-clip-text text-transparent">
                            How FloGPT works
                        </h2>
                    </motion.div>
                    
                    <motion.p 
                        className="mt-8 text-xl leading-8 text-slate-300/90 max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 30 }}
                        animate={isSectionVisible ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        Our platform combines advanced AI, intelligent workflows, and enterprise-grade infrastructure 
                        to deliver chat agents that actually understand and help your customers.
                    </motion.p>
                </motion.div>

                {/* AI Neural Network Timeline */}
                <div ref={stepsRef} className="mx-auto mt-24 max-w-4xl">
                    <div className="relative pl-12">
                        {/* Enhanced AI Network Backbone */}
                        <motion.div
                            className="absolute left-4 top-0 w-0.5 h-full will-change-transform"
                            style={{ 
                                transformOrigin: 'top', 
                                background: 'linear-gradient(180deg, transparent 0%, rgba(59, 130, 246, 0.4) 15%, rgba(139, 92, 246, 0.6) 35%, rgba(16, 185, 129, 0.6) 65%, rgba(249, 115, 22, 0.4) 85%, transparent 100%)',
                                filter: 'blur(1px)',
                            }}
                            initial={{ scaleY: 0, opacity: 0 }}
                            animate={isStepsVisible ? { 
                                scaleY: 1, 
                                opacity: 1,
                                transition: { duration: 2, ease: "easeOut" }
                            } : {}}
                        />

                        {/* AI Data Flow Particles */}
                        <motion.div
                            className="absolute left-3.5 top-0 w-1 h-full will-change-transform"
                            initial={{ opacity: 0 }}
                            animate={isStepsVisible ? { opacity: 1 } : {}}
                            transition={{ delay: 2 }}
                        >
                            {[...Array(12)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute w-2 h-2 rounded-full will-change-transform"
                                    style={{
                                        background: i % 3 === 0 ? 'radial-gradient(circle, rgba(59, 130, 246, 0.8) 0%, transparent 70%)' : 
                                                   i % 3 === 1 ? 'radial-gradient(circle, rgba(139, 92, 246, 0.8) 0%, transparent 70%)' : 
                                                   'radial-gradient(circle, rgba(16, 185, 129, 0.8) 0%, transparent 70%)',
                                    }}
                                    animate={{
                                        y: ["0%", "600%"],
                                        opacity: [0, 1, 1, 0],
                                        scale: [0.5, 1, 1, 0.5],
                                    }}
                                    transition={{
                                        duration: 4,
                                        delay: i * 0.3,
                                        repeat: Infinity,
                                        ease: "linear"
                                    }}
                                />
                            ))}
                        </motion.div>

                        {/* AI Neural Network Connections Between Steps */}
                        {isStepsVisible && steps.map((_, index) => {
                            if (index < steps.length - 1) {
                                return (
                                    <NeuralConnection
                                        key={`connection-${index}`}
                                        from={{ x: 50, y: (index * 320) + 100 }}
                                        to={{ x: 50, y: ((index + 1) * 320) + 100 }}
                                        delay={index * 0.4 + 2.5}
                                        isVisible={isStepsVisible}
                                    />
                                );
                            }
                            return null;
                        })}

                        <div className="space-y-20">
                            {steps.map((step, index) => (
                                <StepCard
                                    key={step.number}
                                    step={step}
                                    index={index}
                                    isVisible={isStepsVisible}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Enhanced Technical Capabilities Grid */}
                <div ref={techRef} className="mt-32">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 40 }}
                        animate={isTechVisible ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.h3 
                            className="text-4xl font-bold text-white mb-6 bg-gradient-to-r from-white via-purple-200 to-cyan-200 bg-clip-text text-transparent"
                            initial={{ scale: 0.9 }}
                            animate={isTechVisible ? { scale: 1 } : {}}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Enterprise-grade technology
                        </motion.h3>
                        <motion.p 
                            className="text-xl text-slate-300/90 max-w-2xl mx-auto"
                            initial={{ opacity: 0 }}
                            animate={isTechVisible ? { opacity: 1 } : {}}
                            transition={{ delay: 0.4 }}
                        >
                            Built with modern, scalable technologies that enterprise businesses trust
                        </motion.p>
                    </motion.div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {technicalFeatures.map((feature, index) => (
                            <TechCard
                                key={feature.title}
                                feature={feature}
                                index={index}
                                isVisible={isTechVisible}
                            />
                        ))}
                    </div>
                </div>

                {/* Enhanced Code Integration Section */}
                <motion.div
                    ref={codeRef}
                    className="mt-32"
                    initial={{ opacity: 0, y: 80 }}
                    animate={isCodeVisible ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                    <div className="relative overflow-hidden rounded-3xl p-12 border border-white/10 backdrop-blur-2xl will-change-transform">
                        {/* Enhanced Background with AI Mesh Pattern */}
                        <motion.div
                            className="absolute inset-0 opacity-30"
                            style={{
                                background: `
                                    radial-gradient(600px circle at 20% 30%, rgba(59, 130, 246, 0.1), transparent 40%),
                                    radial-gradient(400px circle at 80% 70%, rgba(139, 92, 246, 0.1), transparent 40%),
                                    radial-gradient(300px circle at 40% 90%, rgba(16, 185, 129, 0.05), transparent 40%)
                                `
                            }}
                            animate={{
                                scale: [1, 1.05, 1],
                                rotate: [0, 1, 0],
                            }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        />

                        {/* Animated Grid Pattern */}
                        <motion.div
                            className="absolute inset-0 opacity-5"
                            style={{
                                backgroundImage: `
                                    linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                                    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                                `,
                                backgroundSize: '20px 20px'
                            }}
                            animate={{
                                backgroundPosition: ['0px 0px', '20px 20px'],
                            }}
                            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                        />

                        <div className="relative z-10">
                            <motion.div 
                                className="text-center mb-12"
                                initial={{ opacity: 0, y: 30 }}
                                animate={isCodeVisible ? { opacity: 1, y: 0 } : {}}
                                transition={{ delay: 0.3, duration: 0.8 }}
                            >
                                <h3 className="text-3xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                                    One line of code integration
                                </h3>
                                <p className="text-xl text-slate-300/90">
                                    Deploy your chat agent to any website in seconds
                                </p>
                            </motion.div>
                            
                            {/* Enhanced Code Block with Terminal Aesthetics */}
                            <motion.div 
                                className="bg-black/60 rounded-2xl p-8 max-w-3xl mx-auto border border-white/20 backdrop-blur-sm relative overflow-hidden"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={isCodeVisible ? { opacity: 1, scale: 1 } : {}}
                                transition={{ delay: 0.5, duration: 0.8 }}
                            >
                                {/* Terminal Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex space-x-3">
                                        <motion.div 
                                            className="w-4 h-4 bg-red-400 rounded-full cursor-pointer"
                                            whileHover={{ scale: 1.2, boxShadow: "0 0 10px rgba(248, 113, 113, 0.6)" }}
                                            transition={{ duration: 0.2 }}
                                        />
                                        <motion.div 
                                            className="w-4 h-4 bg-yellow-400 rounded-full cursor-pointer"
                                            whileHover={{ scale: 1.2, boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)" }}
                                            transition={{ duration: 0.2 }}
                                        />
                                        <motion.div 
                                            className="w-4 h-4 bg-green-400 rounded-full cursor-pointer"
                                            whileHover={{ scale: 1.2, boxShadow: "0 0 10px rgba(74, 222, 128, 0.6)" }}
                                            transition={{ duration: 0.2 }}
                                        />
                                    </div>
                                    <motion.span 
                                        className="text-sm text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full border border-slate-600"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={isCodeVisible ? { opacity: 1, x: 0 } : {}}
                                        transition={{ delay: 0.8 }}
                                    >
                                        HTML Integration
                                    </motion.span>
                                </div>

                                {/* Typewriter Code Effect */}
                                <motion.div className="relative">
                                    <motion.pre 
                                        className="text-sm text-emerald-300 overflow-x-auto leading-relaxed font-mono"
                                        initial={{ opacity: 0 }}
                                        animate={isCodeVisible ? { opacity: 1 } : {}}
                                        transition={{ delay: 0.7, duration: 1 }}
                                    >
{`<script src="https://yourdomain.com/widget/flogpt.js"></script>
<script>
    FloGPT.create({
        widgetId: 'your-widget-id',
        primaryColor: '#007bff',
        welcomeMessage: 'Hello! How can I help you today?'
    });
</script>`}
                                    </motion.pre>

                                    {/* Animated Cursor */}
                                    <motion.div
                                        className="absolute bottom-4 right-4 w-2 h-5 bg-emerald-400"
                                        animate={{
                                            opacity: [1, 0, 1],
                                        }}
                                        transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            delay: 1.5
                                        }}
                                    />
                                </motion.div>

                                {/* Code Highlight Animation */}
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/5 to-transparent"
                                    animate={isCodeVisible ? {
                                        x: ['-100%', '200%'],
                                        transition: {
                                            duration: 2,
                                            delay: 2,
                                            ease: "easeInOut"
                                        }
                                    } : {}}
                                />
                            </motion.div>
                            
                            {/* Enhanced Bottom Message */}
                            <motion.div 
                                className="text-center mt-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={isCodeVisible ? { opacity: 1, y: 0 } : {}}
                                transition={{ delay: 1, duration: 0.8 }}
                            >
                                <p className="text-slate-300/90 text-lg mb-4">
                                    That's it! Your AI chat agent is now live and ready to help customers 24/7.
                                </p>
                                
                                {/* Success Indicators */}
                                <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mt-6">
                                    {[
                                        { text: 'Instant Deploy', icon: BoltIcon },
                                        { text: 'Secure', icon: ShieldCheckIcon },
                                        { text: 'Analytics Ready', icon: ChartBarIcon }
                                    ].map((item, i) => (
                                        <motion.div
                                            key={item.text}
                                            className="flex items-center space-x-2 text-sm text-slate-400"
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={isCodeVisible ? { opacity: 1, scale: 1 } : {}}
                                            transition={{ delay: 1.2 + (i * 0.1), duration: 0.6 }}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            <span>{item.text}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}